import {
  allocationListSchema,
  daemonSyncSchema,
  hasPermission,
  serverAllocationSchema,
  type ClientServer,
  type ServerAllocation,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Globe, Network, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Confirm } from '@/components/confirm';
import { CopyChip } from '@/components/copy-chip';
import { QueryState } from '@/components/query-state';
import { StatTile } from '@/components/stat-tile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/network')({ component: NetworkTab });

function NetworkTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const queryClient = useQueryClient();
  const key = ['server', id, 'allocations'];
  const base = `/api/client/servers/${id}/network/allocations`;
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => apiRequest(base, { schema: allocationListSchema, signal }),
  });
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: serverQuery(id).queryKey });
  };
  const synced = (r: { daemonSynced: boolean; daemonError: string | null }, ok: string) => {
    if (!r.daemonSynced)
      toast.warning(`Saved, but the node didn't get the change yet: ${r.daemonError}`);
    else toast.success(ok);
    refresh();
  };

  const add = useMutation({
    mutationFn: () =>
      apiRequest(base, {
        method: 'POST',
        schema: serverAllocationSchema.extend(daemonSyncSchema.shape),
      }),
    onSuccess: (r) =>
      synced(r, `Added port ${r.port}. It applies the next time the server starts.`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const makePrimary = useMutation({
    mutationFn: (a: ServerAllocation) =>
      apiRequest(`${base}/${a.id}/primary`, {
        method: 'POST',
        schema: serverAllocationSchema.extend(daemonSyncSchema.shape),
      }),
    onSuccess: (r) =>
      synced(r, `Port ${r.port} is now the main port. Restart the server to use it.`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (a: ServerAllocation) =>
      apiRequest(`${base}/${a.id}`, { method: 'DELETE', schema: daemonSyncSchema }),
    onSuccess: (r) => synced(r, 'Port released.'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const notes = useMutation({
    mutationFn: ({ a, text }: { a: ServerAllocation; text: string }) =>
      apiRequest(`${base}/${a.id}`, {
        method: 'PATCH',
        body: { notes: text || null },
        schema: serverAllocationSchema,
      }),
    onSuccess: () => {
      toast.success('Note saved.');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          The ports this server can use. Players connect to the main port.
        </p>
        {can('allocation.create') && (
          <Button onClick={() => add.mutate()} disabled={add.isPending || list.data?.limit === 0}>
            <Plus /> Add a port
          </Button>
        )}
      </div>
      {list.data && <NetworkSummary server={server} list={list.data} />}
      <QueryState query={list}>
        {({ data }) =>
          data.length === 0 ? (
            <Card className="bg-card/50 border-dashed shadow-none">
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <Network className="text-muted-foreground size-8" aria-hidden />
                <p className="font-medium">No ports</p>
              </CardContent>
            </Card>
          ) : (
            <ul className="grid gap-3">
              {data.map((a) => (
                <li key={a.id}>
                  <Card
                    className={cn(
                      'flex-row flex-wrap items-center justify-between gap-3 px-4 py-3',
                      a.isPrimary && 'border-primary/40 bg-primary/[0.04]',
                    )}
                  >
                    <div className="grid min-w-0 gap-1">
                      <p className="flex flex-wrap items-center gap-2">
                        <CopyChip value={`${a.ipAlias ?? a.ip}:${a.port}`} label="address" />
                        {a.isPrimary && (
                          <Badge variant="info">
                            <Star /> Main
                          </Badge>
                        )}
                      </p>
                      <NoteField
                        allocation={a}
                        editable={can('allocation.update')}
                        onSave={(text) => notes.mutate({ a, text })}
                      />
                    </div>
                    <div className="flex gap-2">
                      {!a.isPrimary && can('allocation.update') && (
                        <Button variant="outline" size="sm" onClick={() => makePrimary.mutate(a)}>
                          <Star /> Make main
                        </Button>
                      )}
                      {!a.isPrimary && can('allocation.delete') && (
                        <Confirm
                          title={`Release port ${a.port}?`}
                          description="It goes back to the node's pool and the server stops listening on it after the next start."
                          action="Release"
                          destructive
                          onConfirm={() => remove.mutate(a)}
                        >
                          <Button
                            variant="outline-destructive"
                            size="icon"
                            aria-label={`Release port ${a.port}`}
                          >
                            <Trash2 />
                          </Button>
                        </Confirm>
                      )}
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
    </div>
  );
}

/** Where players connect and how many extra ports are in use. */
function NetworkSummary({
  server,
  list,
}: {
  server: ClientServer;
  list: { data: ServerAllocation[]; limit: number };
}) {
  const main = list.data.find((a) => a.isPrimary);
  const extra = Math.max(0, list.data.length - 1);
  return (
    <section
      aria-label="Network summary"
      className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
    >
      {main && (
        <StatTile
          icon={Globe}
          label="Players connect with"
          value={
            <CopyChip
              value={`${main.ipAlias ?? main.ip}:${main.port}`}
              label="server address"
              className="text-sm"
            />
          }
          detail={`Hosted on ${server.node.name} · ${server.node.location.long ?? server.node.location.short}`}
        />
      )}
      <StatTile
        icon={Network}
        label="Extra ports"
        value={extra}
        limit={`of ${list.limit}`}
        fraction={list.limit > 0 ? extra / list.limit : null}
        detail={
          list.limit === 0
            ? 'An administrator hasn’t allowed extra ports'
            : extra >= list.limit
              ? 'All extra ports are in use'
              : `${list.limit - extra} more can be added`
        }
      />
    </section>
  );
}

function NoteField({
  allocation,
  editable,
  onSave,
}: {
  allocation: ServerAllocation;
  editable: boolean;
  onSave: (text: string) => void;
}) {
  const [text, setText] = useState(allocation.notes ?? '');
  if (!editable)
    return <p className="text-muted-foreground text-xs">{allocation.notes ?? 'No note'}</p>;
  return (
    <Input
      aria-label={`Note for port ${allocation.port}`}
      className="h-8 max-w-xs text-xs"
      placeholder="Add a note (for example: voice chat)"
      value={text}
      maxLength={191}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text !== (allocation.notes ?? '') && onSave(text)}
    />
  );
}
